import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Button,
  TextField,
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { RiskScoreChip } from "../../common/RiskScoreChip";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_RISK_ASSESSMENT_ITEM } from "../../../../../../apis/organization-apis/risk-apis/riskApis";
import { GET_MASTER_LOOKUP_BY_TYPE } from "../../../../../../apis/common-apis/commonApis";
import ConfirmSnackbar from "../../../../../../components/Snackbar/ConfirmSnackbar";
import {
  EmptyFields,
  MasterLookupItemsType,
} from "../../../../../../utils/enums";

const HighResidualRiskDialog = ({
  open,
  onClose,
  missingCommentsRisks = [],
  missingCommentsReasons = {},
  setMissingCommentsReasons = () => { },
  completeAssessment = () => { },
}) => {
  const theme = useTheme();
  const [currentRiskIndex, setCurrentRiskIndex] = useState(0);
  const [riskTreatments, setRiskTreatments] = useState({});
  const [assessmentSnackbar, setAssessmentSnackbar] = useState(null);
  const [updateAssessmentItem, { loading: updateAssessmentItemLoading }] =
    useMutation(UPDATE_RISK_ASSESSMENT_ITEM);

  const { data: treatmentData } = useQuery(GET_MASTER_LOOKUP_BY_TYPE, {
    variables: { type: MasterLookupItemsType.RISK_TREATMENT },
  });
  const treatments = treatmentData?.getMasterLookupByType?.data || [];

  const handleNext = () => {
    if (currentRiskIndex < missingCommentsRisks?.length - 1) {
      setCurrentRiskIndex(currentRiskIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentRiskIndex > 0) {
      setCurrentRiskIndex(currentRiskIndex - 1);
    }
  };

  const handleUpdateIndividualRisk = async (riskId, comment, treatment) => {
    if (!comment || comment.trim() === "") {
      return;
    }

    try {
      const updateData = { comments: comment.trim() };

      if (treatment) {
        const selectedTreatment = treatments.find((t) => t.title === treatment);
        if (selectedTreatment) {
          updateData.treatment_id = selectedTreatment.id;
        }
      }

      const { data } = await updateAssessmentItem({
        variables: {
          assessment_item_id: parseInt(riskId),
          update_data: updateData,
        },
      });

      if (data?.updateRiskAssessmentItem?.success) {
        setAssessmentSnackbar({
          message: "Risk assessment updated successfully!",
          vertical: "top",
          horizontal: "right",
        });

        if (missingCommentsRisks?.length - 1 === currentRiskIndex) {
          onClose(true);
          completeAssessment();
        } else {
          if (currentRiskIndex < missingCommentsRisks?.length) {
            setCurrentRiskIndex(currentRiskIndex + 1);
          }
        }
      } else {
        setAssessmentSnackbar({
          message:
            data?.updateRiskAssessmentItem?.message ||
            "Failed to update risk assessment",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setAssessmentSnackbar({
        message: `Error updating risk assessment: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  useEffect(() => {
    if (open && missingCommentsRisks?.length > 0) {
      setCurrentRiskIndex(0);
    }
  }, [open, missingCommentsRisks?.length]);

  return (
    <>
      <ConfirmSnackbar snackbarInfo={assessmentSnackbar} />
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: theme.customColors.shadow.main,
            border: `1px solid ${theme.customColors.border.main}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1.5,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            backgroundColor: theme.customColors.background.cardHeader,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "6px",
              backgroundColor: theme.customColors.status.error.bg,
              color: theme.customColors.status.error.text,
            }}
          >
            <WarningIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{ fontSize: "16px", color: theme.customColors.text.strong }}
            >
              Missing Comments for High-Risk Items
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                mt: 0.25,
                color: theme.customColors.text.secondary,
              }}
            >
              Add comments for high-risk items before submitting assessment
            </Typography>
          </Box>
          <IconButton
            onClick={() => onClose(false)}
            sx={{
              position: "absolute",
              right: 10,
              top: 10,
              color: theme.customColors.text.secondary,
              "&:hover": {
                backgroundColor: theme.customColors.background.hover,
                color: theme.customColors.text.strong,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: "6px",
              border: `1px solid ${theme.customColors.status.warning.border}`,
              backgroundColor: theme.customColors.status.warning.bg,
              py: 1,
              "& .MuiAlert-icon": {
                color: theme.customColors.status.warning.text,
              },
              "& .MuiAlert-message": {
                color: theme.customColors.status.warning.text,
                fontWeight: 500,
                fontSize: "13px",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, fontSize: "13px", lineHeight: 1.4 }}
            >
              The following {missingCommentsRisks?.length} high-risk item
              {missingCommentsRisks?.length > 1 ? "s" : ""}{" "}
              {missingCommentsRisks?.length > 1 ? "are" : "is"} missing required
              comments. Please add detailed comments for each item before
              submitting the assessment.
            </Typography>
          </Alert>

          {missingCommentsRisks?.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                  p: 2,
                  backgroundColor: theme.customColors.background.hover,
                  borderRadius: "12px",
                  border: `1px solid ${theme.customColors.border.main}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <IconButton
                  onClick={handlePrevious}
                  disabled={currentRiskIndex === 0}
                  sx={{
                    color:
                      currentRiskIndex === 0
                        ? theme.customColors.text.disabled
                        : theme.customColors.text.secondary,
                    backgroundColor:
                      currentRiskIndex === 0
                        ? "transparent"
                        : theme.customColors.background.hover,
                    borderRadius: "8px",
                    p: 1,
                    "&:hover": {
                      backgroundColor:
                        currentRiskIndex === 0
                          ? "transparent"
                          : theme.customColors.border.main,
                      transform: "translateX(-2px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 22 }} />
                </IconButton>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={theme.customColors.text.strong}
                    sx={{ fontSize: "16px" }}
                  >
                    Risk {currentRiskIndex + 1} of{" "}
                    {missingCommentsRisks?.length}
                  </Typography>
                </Box>

                <IconButton
                  onClick={handleNext}
                  disabled={
                    currentRiskIndex === missingCommentsRisks?.length - 1
                  }
                  sx={{
                    color:
                      currentRiskIndex === missingCommentsRisks?.length - 1
                        ? theme.customColors.text.disabled
                        : theme.customColors.text.secondary,
                    backgroundColor:
                      currentRiskIndex === missingCommentsRisks?.length - 1
                        ? "transparent"
                        : theme.customColors.background.hover,
                    borderRadius: "8px",
                    p: 1,
                    "&:hover": {
                      backgroundColor:
                        currentRiskIndex === missingCommentsRisks?.length - 1
                          ? "transparent"
                          : theme.customColors.border.main,
                      transform: "translateX(2px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Box>

              {/* Current Risk Card */}
              {(() => {
                const risk = missingCommentsRisks[currentRiskIndex];
                return (
                  <Card
                    sx={{
                      border: `1px solid ${theme.customColors.border.main}`,
                      borderRadius: "12px",
                      backgroundColor: theme.customColors.background.card,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                      overflow: "hidden",
                      "&:hover": {
                        boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                        borderColor: theme.customColors.border.hover,
                        transition: "all 0.3s ease",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          mb: 2,
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                borderRadius: "6px",
                                backgroundColor: theme.palette._blue.main,
                                color: "white",
                                fontSize: "12px",
                                fontWeight: 700,
                              }}
                            >
                              {currentRiskIndex + 1}
                            </Box>
                            {/* Risk Title */}
                            <Typography
                              variant="h5"
                              fontWeight={600}
                              sx={{ color: theme.customColors.text.strong }}
                            >
                              {risk?.risk_title || EmptyFields.TITLE}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flexWrap: "wrap",
                              mb: 1,
                            }}
                          >
                            <RiskScoreChip score={risk?.residual_risk_score} />
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <SecurityIcon
                                sx={{
                                  fontSize: 14,
                                  color: theme.customColors.text.secondary,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.customColors.text.secondary,
                                  fontSize: "11px",
                                }}
                              >
                                Owner:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.customColors.text.strong,
                                  fontWeight: 600,
                                  fontSize: "11px",
                                }}
                              >
                                {risk?.risk_owner_email || EmptyFields.OWNER}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AssignmentIcon
                                sx={{
                                  fontSize: 14,
                                  color: theme.customColors.text.secondary,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.customColors.text.secondary,
                                  fontSize: "11px",
                                }}
                              >
                                Category:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.customColors.text.strong,
                                  fontWeight: 600,
                                  fontSize: "11px",
                                }}
                              >
                                {risk?.risk_category_title ||
                                  EmptyFields.RISK_CATEGORY}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* Risk Description */}
                      {risk?.risk_description && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.6,
                              color: theme.customColors.text.main,
                              backgroundColor:
                                theme.customColors.background.light,
                              p: 1.5,
                              borderRadius: 1,
                              border: `1px solid ${theme.customColors.border.main}`,
                            }}
                          >
                            {risk?.risk_description || EmptyFields.DESCRIPTION}
                          </Typography>
                        </Box>
                      )}

                      {/* Treatment and Comments Section */}
                      <Box sx={{ mb: 2 }}>
                        {/* Treatment Selection - Row 1 */}
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              mb: 1,
                              fontSize: "12px",
                              color: theme.customColors.text.strong,
                            }}
                          >
                            Risk Treatment
                          </Typography>
                          <FormControl fullWidth size="small">
                            <InputLabel>Select Treatment</InputLabel>
                            <Select
                              value={
                                riskTreatments[risk?.id] ||
                                risk?.treatment_title ||
                                ""
                              }
                              onChange={(e) =>
                                setRiskTreatments((prev) => ({
                                  ...prev,
                                  [risk.id]: e.target.value,
                                }))
                              }
                              label="Select Treatment"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor:
                                    theme.customColors.background.card,
                                  "&:hover": {
                                    borderColor: theme.customColors.border.main,
                                  },
                                  "&.Mui-focused": {
                                    borderColor: theme.palette._blue.main,
                                    boxShadow:
                                      "0 0 0 2px rgba(59, 130, 246, 0.1)",
                                  },
                                },
                              }}
                            >
                              {treatments.map((treatment) => (
                                <MenuItem
                                  key={treatment?.id}
                                  value={treatment?.title}
                                >
                                  {treatment?.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Comments Input - Row 2 */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              mb: 1,
                              fontSize: "12px",
                              color: theme.customColors.text.strong,
                            }}
                          >
                            Comments * (Required)
                          </Typography>
                          <TextField
                            value={missingCommentsReasons[risk.id] || ""}
                            onChange={(e) =>
                              setMissingCommentsReasons((prev) => ({
                                ...prev,
                                [risk.id]: e.target.value,
                              }))
                            }
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={6}
                            required
                            placeholder="Add detailed comments explaining the risk assessment, treatment decisions, or any other relevant information..."
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor:
                                  theme.customColors.background.card,
                                borderRadius: "8px",
                                border: `1px solid ${theme.customColors.border.main}`,
                                "&:hover": {
                                  borderColor: theme.customColors.border.main,
                                },
                                "&.Mui-focused": {
                                  borderColor: theme.palette._blue.main,
                                  boxShadow:
                                    "0 0 0 2px rgba(59, 130, 246, 0.1)",
                                },
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "15px",
                                fontWeight: 400,
                                lineHeight: 1.4,
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.5,
                              display: "block",
                              color: theme.customColors.text.secondary,
                              fontWeight: 500,
                              fontSize: "10px",
                            }}
                          >
                            Provide detailed comments about this risk
                            assessment, including treatment decisions,
                            mitigation plans, or acceptance criteria.
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Enhanced Submit Button */}
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          variant="contained"
                          size="medium"
                          disabled={
                            !missingCommentsReasons[risk?.id] ||
                            missingCommentsReasons[risk?.id].trim() === "" ||
                            updateAssessmentItemLoading
                          }
                          onClick={() =>
                            handleUpdateIndividualRisk(
                              risk.id,
                              missingCommentsReasons[risk.id],
                              riskTreatments[risk.id]
                            )
                          }
                          startIcon={
                            updateAssessmentItemLoading ? (
                              <CircularProgress size={18} />
                            ) : null
                          }
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            px: 3,
                            py: 1,
                            borderRadius: "8px",
                            backgroundColor:
                              theme.customColors.button.success.main,
                            color: "white",
                            boxShadow: "0 2px 4px rgba(16, 185, 129, 0.2)",
                            "&:hover": {
                              backgroundColor:
                                theme.customColors.button.success.dark,
                              boxShadow: "0 4px 8px rgba(16, 185, 129, 0.3)",
                              transform: "translateY(-1px)",
                            },
                            "&:disabled": {
                              backgroundColor: theme.customColors.text.disabled,
                              color: theme.customColors.text.secondary,
                              boxShadow: "none",
                              transform: "none",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {updateAssessmentItemLoading
                            ? "Updating..."
                            : "Update Risk Assessment"}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })()}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HighResidualRiskDialog;
